<template>
  <div v-for="(table, key) in tableData.tableSections" :key="key">
    <div class="text-racq-dark-grey text-lg-bold mt-6 mb-3">
      <RichText :field="table.heading" />
    </div>
    <div
      v-if="table.tableColumn && table.tableColumn.length"
      class="grid grid-flow-col gap-x-2 md:gap-x-3"
      :style="
        getGridTemplate(
          table.tableColumn.length,
          table.tableColumn[0].rows.length,
          toBool(table.showMoreButton?.value),
          toNumber(table.initialNumberOfItemsToShow?.value)
        )
      "
    >
      <div v-for="(tableColumn, index) in table.tableColumn" :key="index" class="contents">
        <div
          v-for="(tableRow, row) in toBool(table.showMoreButton?.value)
            ? isExpanded
              ? tableColumn.rows
              : tableColumn.rows.slice(0, toNumber(table.initialNumberOfItemsToShow?.value))
            : tableColumn.rows"
          :key="row"
          class="p-4 border border-racq-blue-background"
          :class="[
            tableColumn.highlighted ? 'bg-racq-anti-flash-white' : '',
            getBorderClass(
              row,
              tableColumn,
              toBool(table.showMoreButton?.value),
              toNumber(table.initialNumberOfItemsToShow?.value)
            ),
            row == 0 ? 'rounded-t' : '',
            row == tableColumn.rows.length - 1 ? 'rounded-b' : '',
          ]"
        >
          <div class="flex flex-col gap-1">
            <div class="flex text-m-bold text-racq-dark-grey">
              <div>
                <RichText tag="span" :field="tableRow.heading" />
                <button
                  v-if="tableRow.tooltipContent?.value"
                  :id="`tooltip-row${row}-column${index}`"
                  type="button"
                  class="questionmark-icon js-tooltip-trigger -mt-0.5 inline-block"
                  aria-label="show tooltip"
                  @click="
                    (e) => {
                      showTooltipOnTriggerClick(e, tableRow.tooltipContent?.value ?? '');
                    }
                  "
                ></button>
              </div>
            </div>
            <p class="text-m text-dark-grey-text">
              <RichText :field="tableRow.description" />
            </p>
            <PercentageBar v-if="tableRow.percentage?.value" :percentage="tableRow.percentage.value" />
          </div>
        </div>
      </div>
    </div>
    <ShowHideButton
      v-if="toBool(table.showMoreButton?.value)"
      :showMore="isExpanded"
      :showLessLabel="table.showLessButtonText?.value"
      :showMoreLabel="table.showMoreButtonText?.value"
      @click="toggleContent"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { ITableGroup, ITableGroupColumn } from '@/interfaces/entities/table-group.interface';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import PercentageBar from '@/components/PercentageBar/PercentageBar.vue';
import ShowHideButton from '@/components/ShowHideButton/ShowHideButton.vue';
import { toBool, toNumber } from '@/utils/convert';
import { showTooltipOnTriggerClick } from '@/plugins/tooltip/renderTooltip';

// Define props and emitters
// ------------------------------------------------------------- //
defineProps<{
  tableData: ITableGroup;
}>();

const isExpanded = ref(false);

// Define Functions
// ------------------------------------------------------------- //
const getGridTemplate = (numberOfColumns: number, numberOfRow: number, showMore?: boolean, numberOfItems?: number) => {
  return {
    gridTemplateRows: `${showMore && !isExpanded.value ? `repeat(${numberOfItems}, minmax(0, auto))` : `repeat(${numberOfRow}, minmax(0, auto))`}`,
    gridTemplateColumns: `repeat(${numberOfColumns}, minmax(0, 1fr))`,
  };
};
const getBorderClass = (
  index: number,
  tableColumn: ITableGroupColumn,
  showMore?: boolean,
  initialNumberOfItemsToShow?: number
) => {
  if (showMore) {
    return !isExpanded.value && index == initialNumberOfItemsToShow! - 1
      ? ''
      : index == tableColumn.rows.length - 1
        ? ''
        : 'border-b-0 border-b-0';
  } else {
    return index == tableColumn.rows.length - 1 ? '' : 'border-b-0 border-b-0';
  }
};

const toggleContent = () => {
  isExpanded.value = !isExpanded.value;
};
const showModal = (title?: string, content?: string) => {
  (window as any)?.showRACQTooltipModal(title, content);
};
</script>
<style>
.table-item-grid {
  grid-template-rows: auto;
}
</style>
